import { useIonModal } from '@ionic/react'

import { SiteSettingsModal } from '@apps-common/apps/my-monitoring/modals/site-settings/SiteSettingsModal'
import { CardContainer, ImageComponent, LoadingSwitcher } from '@apps-common/components/common'
import { SiteDetailsModal } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { useGetDefaultSiteImage } from '@apps-common/hooks/useGetDefaultSiteImage'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { useMyMonitoringSiteContextStore } from '@apps-common/stores'
import { IMMSiteModel } from '@apps-common/types/my-monitoring'

const SiteDetailsSkeleton: React.FC = () => (
  <div className='rounded-[30px] min-h-[92px] !p-4 !pr-6 w-full h-full overflow-hidden animate-pulse'>
    <div className='flex items-center justify-center gap-[20px]'>
      <div className='w-[60px] h-[60px] rounded-xl bg-skeleton-hi' />

      <div className='grow flex flex-col items-start justify-between gap-[10px]'>
        <div className='bg-skeleton-hi h-[22px] w-[100%] rounded-lg' />
        <div className='flex items-center justify-start gap-2'>
          <div className='bg-skeleton-hi rounded-full h-[24px] w-[66px]' />
          <div className='bg-skeleton-hi rounded-full h-[24px] w-[66px]' />
        </div>
      </div>
    </div>
  </div>
)

interface IProps {
  siteLoading?: boolean
  site?: IMMSiteModel
  onSiteChange: (id: string) => void
}

export const SiteDetails: React.FC<IProps> = ({ siteLoading, site, onSiteChange }) => {
  const { isAdmin } = useMyMonitoringSiteContextStore()
  const { loading: defaultImageLoading, defaultImageUrl } = useGetDefaultSiteImage(site?.siteId)

  const [presentDetails, dismissDetails] = useIonModal(SiteDetailsModal, {
    componentProps: {
      unitId: site?.unitId,
      hideDelete: true,
      disableEditing: !isAdmin
    },
    onDismiss: (modalData: string, role: string) => dismissDetails(null, role)
  })

  const presentSiteDetails = () => {
    presentDetails({
      enterAnimation: modalSlideUpEnterAnimation,
      leaveAnimation: modalSlideUpLeaveAnimation,
      backdropDismiss: true
    })
  }

  const [presentSettings, dismissSettings] = useIonModal(SiteSettingsModal, {
    componentProps: {
      site
    },
    onDismiss: (modalData: string, role: string) => dismissSettings(null, role)
  })

  const presentSiteSettings = () => {
    presentSettings({
      enterAnimation: modalSlideUpEnterAnimation,
      leaveAnimation: modalSlideUpLeaveAnimation,
      backdropDismiss: true
    })
  }

  const loading = siteLoading || defaultImageLoading

  return (
    <LoadingSwitcher
      key='mm-site-details'
      loading={!site || loading}
      skeleton={<SiteDetailsSkeleton />}
      classes='w-full grow overflow-hidden'
    >
      <CardContainer bgClasses='text-ctx-text !py-4 w-full h-full rounded-[22px]'>
        <div className='flex items-center justify-center gap-4'>
          <button type='button' onClick={() => presentSiteDetails()}>
            <ImageComponent
              src={site?.siteImageUrl || defaultImageUrl}
              alt={site?.siteName || `Site ${site?.accountNo}`}
              containerClasses='w-[50px] h-[50px] rounded-xl overflow-hidden border border-border'
              imageClasses='min-w-[100%] min-h-[100%] object-cover'
              errorIconClasses='w-[50px] h-[50px]'
              ignoreContainerOnError
            />
          </button>
          <div className='flex flex-col items-start justify-between gap-2'>
            <h2 className='text-[0.9rem] jost opacity-70'>{site?.siteName || `Site - Acc# ${site?.accountNo}`}</h2>
            <div className='w-full flex items-center justify-start gap-3'>
              <button
                type='button'
                className='!bg-ctx-mm-brand !text-ctx-on-mm-brand text-[0.85rem] jost flex items-center justify-between gap-[10px] rounded-full pl-[12px] p-[3px]'
                onClick={() => presentSiteDetails()}
              >
                Details
                <div className='rounded-full bg-ctx-on-mm-brand/[0.15] p-[2px]'>
                  <commonIcons.arrowAngled className='text-ctx-on-mm-brand text-[0.9rem]' />
                </div>
              </button>

              <button
                type='button'
                className='!bg-ctx-mm-brand !text-ctx-on-mm-brand text-[0.85rem] jost flex items-center justify-between gap-[8px] rounded-full pl-[12px] p-[3px]'
                onClick={() => presentSiteSettings()}
              >
                Settings
                <div className='rounded-full bg-ctx-on-mm-brand/[0.15] p-[3px]'>
                  <commonIcons.arrowAngled className='rotate-45 text-ctx-on-mm-brand text-[0.9rem]' />
                </div>
              </button>
            </div>
          </div>
        </div>
      </CardContainer>
    </LoadingSwitcher>
  )
}
